/**
 * Chameleon (product-adoption SDK) boundary. Nothing else imports
 * `@chamaeleonidae/chmln` directly, because the vendor package is not safe to
 * call as-is:
 *
 * - `identify` and `track` dereference a bare `chmln` global, so they throw
 *   ReferenceError — not a silent no-op — whenever `init` was skipped. Init is
 *   skipped in development, and the script can also fail to load in production
 *   (adblockers block product-adoption SDKs), so every call site would otherwise
 *   need its own guard.
 * - `clear`, `on` and `show` are not exported by the package at all. They exist
 *   only as queued methods on the `window.chmln` object that `init` installs.
 *
 * Net effect: when init is skipped, every call here degrades to a no-op.
 */
import * as chmln from '@chamaeleonidae/chmln'
import { CHAMELEON_ACCOUNT_TOKEN, ENV } from '@/config/env'

// Required for accounts created after 2022-05-21; the package still defaults to
// the legacy fast.trychameleon.com host.
const FAST_URL = 'https://fast.chameleon.io/'

/** The queue object `init` installs on `window` — the only route to clear/on. */
declare global {
  interface Window {
    chmln?: {
      clear?: () => void
      on?: (
        event: 'app:navigate',
        handler: (opts: { to?: string }) => void
      ) => void
      show?: (
        experienceId: string,
        options?: { redirect?: boolean; skip_triggers?: boolean }
      ) => void
      /** Planted by the remote script, never by the bootstrap queue. */
      root?: unknown
    }
  }
}

/**
 * Company data must carry a real uid — Chameleon reads a present-but-null
 * `company.uid` as a broken account link, so the key is omitted instead.
 */
export type ChameleonCompany = { uid: string } & Record<string, unknown>

/**
 * The bootstrap queue `init` installs synchronously. Calls made through it are
 * buffered and replayed once the remote script arrives, so this — not `isReady`
 * — is what identify/track gate on: dropping them here would lose the identify
 * that fires immediately after init.
 */
const isBootstrapped = () =>
  typeof window !== 'undefined' && window.chmln !== undefined

/**
 * Whether the remote script itself loaded. The bootstrap sets an exhaustive,
 * readable list of properties on the queue — accountToken, location, fastUrl,
 * now, and one stub per queued method — and `root` is not among them, so only
 * messo.min.js can have planted it. That separates a loaded SDK from a queue
 * that will buffer forever because an adblocker or an offline network ate the
 * script. It is an undocumented vendor internal and no lockfile pins it, which
 * is what the give-up warning below is for.
 */
const isReady = () => isBootstrapped() && window.chmln!.root !== undefined

// `root` lands whenever the network delivers messo.min.js, which can be after a
// David answer has finished streaming — and a finished message never re-renders
// on its own. So readiness is a subscribable value, not a one-shot read.
const POLL_MS = 250
// Past this, the script is not arriving: blocked, offline, or Chameleon moved
// the internal flag `isReady` reads. Whichever it is, no tour control will ever
// render, so stop watching and say so once rather than fail silently forever.
const GIVE_UP_MS = 20_000

const readyListeners = new Set<() => void>()
let poll: ReturnType<typeof setInterval> | undefined
let gaveUp = false

const stopWatching = () => {
  clearInterval(poll)
  poll = undefined
}

/**
 * The SDK offers no load callback, so `root` has to be watched for. Gated on
 * the queue existing: in development `init` is skipped, so nothing was ever
 * installed and the warning below would be describing a network failure that
 * did not happen — the one environment where a hidden control is correct.
 */
function startWatching(): void {
  if (poll || gaveUp || !isBootstrapped() || isReady()) return

  // At most one watch per session — the guard above means it can only end at
  // ready or at give-up, and both are terminal — so this deadline is per
  // session rather than per interval.
  const startedAt = Date.now()
  poll = setInterval(() => {
    if (isReady()) {
      stopWatching()
      for (const listener of readyListeners) listener()
      return
    }
    if (Date.now() - startedAt < GIVE_UP_MS) return

    stopWatching()
    gaveUp = true
    console.warn(
      '[chameleon] bootstrap queue installed but the remote script never loaded — tour controls will stay hidden'
    )
  }, POLL_MS)
}

/**
 * `useSyncExternalStore` subscribe half, paired with `isReady` as the snapshot.
 * Fires at most once per session: readiness only ever goes false -> true.
 *
 * Unsubscribing deliberately leaves the watch running. It has two jobs and only
 * notification is tied to having subscribers — stopping it when the last tour
 * fence unmounts would cancel the give-up warning that `init` started it for.
 * It is bounded either way: it ends at ready or at give-up.
 */
const subscribeReady = (listener: () => void): (() => void) => {
  readyListeners.add(listener)
  startWatching()
  return () => {
    readyListeners.delete(listener)
  }
}

export const chameleon = {
  /** Whether the SDK actually loaded. Callers that offer UI gate on this. */
  isReady,

  /** Re-render hook for that gate, since the SDK can arrive mid-conversation. */
  subscribeReady,

  /**
   * Starts a Tour by id. Chameleon's own `redirect` stays off because it
   * hard-navigates, which reloads the SPA and loses the identified session —
   * the app routes client-side instead. `honorTriggers` is true only once the
   * app has navigated, so Chameleon waits for step 1's own anchor rather than
   * the app guessing a delay; a tour launched in place has no anchor to wait
   * for and an unmet trigger would swallow the click.
   */
  show(tourId: string, { honorTriggers }: { honorTriggers: boolean }): void {
    window.chmln?.show?.(tourId, {
      redirect: false,
      skip_triggers: !honorTriggers,
    })
  },

  /**
   * Loads the SDK. Idempotent: re-entry is normal here (StrictMode double-invokes
   * effects, `bun --hot` re-runs modules) and the vendor `init` logs a warning
   * and bails when the global already exists.
   */
  init(): void {
    // No analytics SDK in development, matching PostHog
    // (contexts/PostHogProviderWrapper.tsx). Guarding init alone is enough:
    // nothing is ever installed, so every other method here no-ops. This also
    // keeps local sessions off the MTU quota, which identify would otherwise
    // consume since the verification secret is present in local .env.
    if (ENV === 'development') return
    // Re-entry guard, so it is the queue and not the load that matters here:
    // the vendor `init` only logs a warning and bails once the global exists.
    if (isBootstrapped()) return
    chmln.init(CHAMELEON_ACCOUNT_TOKEN, { fastUrl: FAST_URL })
    // No subscriber needed: the give-up warning is worth having on its own.
    startWatching()
  },

  identify(
    userId: string,
    properties: Record<string, unknown>,
    company: ChameleonCompany | null
  ): void {
    if (!isBootstrapped()) return
    chmln.identify(userId, company ? { ...properties, company } : properties)
  },

  track(event: string): void {
    if (!isBootstrapped()) return
    chmln.track(event)
  },

  clear(): void {
    window.chmln?.clear?.()
  },

  /** Lets Chameleon experiences deep-link through the app's own router. */
  onNavigate(handler: (to: string) => void): void {
    window.chmln?.on?.('app:navigate', (opts) => {
      if (opts.to) handler(opts.to)
    })
  },
}
